import './App.scss';
import React, {Suspense, lazy, useEffect, useState} from 'react';
import {Routes, Route, useLocation, useNavigate} from 'react-router-dom';
import Grid from '@mui/material/Grid';
import MenuBar from './components/MenuBar';
import Footer from './components/pages/Footer';
import RequireAdmin from './components/auth/RequireAdmin';
import RequireAuth from './components/auth/RequireAuth';
import { useSessionData } from './components/auth/SessionDataProvider';
import {StreamerPage} from "./components/pages/StreamerPage";
import {CircularProgress} from "@mui/material";
import ShowSnack from "./components/util/ShowSnack";

// Lazy-loaded pages
const MainPage = lazy(() => import('./components/pages/MainPage'));
const WelcomePage = lazy(() => import('./components/pages/WelcomePage').then(m => ({ default: m.WelcomePage })));
const ViewSkrach = lazy(() => import('./components/modules/skrach/ViewSkrach'));
const GameField = lazy(() => import('./components/pages/GameField'));
const ViewScoreboard = lazy(() => import('./components/modules/skrach/ViewScoreboard'));
const SkrachWizard = lazy(() => import('./components/modules/skrach/SkrachWizard'));
const SearchCategory = lazy(() => import('./components/pages/SearchCategory'));
const FreshSkraches = lazy(() => import('./components/pages/FreshSkraches'));
const TrendingSkraches = lazy(() => import('./components/pages/TrendingSkraches'));
const TopRatedSkraches = lazy(() => import('./components/pages/TopRatedSkraches'));
const BrowsePage = lazy(() => import('./components/pages/BrowsePage'));
const ViewerVotePanel = lazy(() => import('./components/pages/ViewerVotePanel'));
const CreateRoomPanel = lazy(() => import('./components/pages/CreateRoomPanel'));
const SignInPage = lazy(() => import('./components/pages/SignInPage'));
const CreateNickname = lazy(() => import('./components/pages/CreateNickname'));
const UserSkraches = lazy(() => import('./components/pages/UserSkraches'));
const EditSkrach = lazy(() => import('./components/modules/skrach/EditSkrach'));
const CreateQuestion = lazy(() => import('./components/modules/skrach/CreateQuestion'));
const CreateCategory = lazy(() => import('./components/modules/category/CreateCategory'));
const ListAllSkrach = lazy(() => import('./components/modules/skrach/ListAllSkrach'));
const ListWaitingForApproval = lazy(() => import('./components/pages/ListWaitingForApproval'));
const AdminDashboard = lazy(() => import('./components/auth/AdminDashboard'));
const ListFeedback = lazy(() => import('./components/auth/ListFeedback'));
const ViewFeedback = lazy(() => import('./components/auth/ViewFeedback'));
const ListScores = lazy(() => import('./components/auth/ListScores'));
const CreateGameMode = lazy(() => import('./components/create/CreateGameMode'));
const FeedbackPage = lazy(() => import('./components/pages/FeedbackPage'));
const GameFieldAutoplay = lazy(() => import('./components/pages/GameFieldAutoplay'));
const AutoplayMain = lazy(() => import('./components/modules/elements/livestream/AutoplayMain'));
const LiveStreamPage = lazy(() => import('./components/modules/elements/livestream/LiveStreamPage'));
const AboutUs = lazy(() => import('./components/pages/AboutUs'));
const GameFieldShorts = lazy(() => import('./components/pages/GameFieldShorts'));
const GameFieldAutoplayManual = lazy(() => import('./components/pages/GameFieldAutoplayManual'));
const MainPageCover = lazy(() => import('./components/pages/MainPageCover'));
const TagsPage = lazy(() => import('./components/pages/TagsPage'));
const AddCarousel = lazy(() => import('./components/create/AddCarousel'));
const MemoryGame = lazy(() => import('./components/modules/games/MemoryGame'));

function App() {
    const { isSmallScreen, loading, isOnPath, isPathStartsWith } = useSessionData();
    const location = useLocation();
    const navigate = useNavigate();
    const isVotingPage = location.pathname.startsWith('/vote/');

    const [snackOpen, setSnackOpen] = useState(false);

    // useEffect(() => {
    //     const isAtRoot = location.pathname === '/';
    //     const hasVisited = localStorage.getItem('visited');
    //
    //     if (isAtRoot && !hasVisited) {
    //         localStorage.setItem('visited', 'true');
    //         navigate('/welcome', { replace: true });
    //     }
    // }, [location, navigate]);

    useEffect(() => {
        const isIos = () => {
            const userAgent = window.navigator.userAgent.toLowerCase();
            return /iphone|ipad|ipod/.test(userAgent);
        };

        const isInStandaloneMode = () =>
            'standalone' in window.navigator && window.navigator.standalone;

        if (isIos() && !isInStandaloneMode()) {
            const shown = localStorage.getItem('iosInstallPromptShown');
            if (!shown) {
                localStorage.setItem('iosInstallPromptShown', 'true');
                setSnackOpen(true);
            }
        }
    }, []);

    if (loading) {
        return (
            <div className="App">
                <MenuBar />
            </div>
        );
    }

    return (
        <div className="App">
            <MenuBar />

            <Grid container spacing={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <Grid item xs={12} lg={12} xl={isOnPath('/live') ? 12 : isOnPath('/cover') ? 12 : 11} sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'flex-start', alignItems: 'flex-start', justifyContent: 'flex-start', height: 'auto', minHeight: 'auto' }}>

                    <Suspense fallback={<CircularProgress />}>
                        <Routes>
                            <Route index path="/" element={<MainPage />} />
                            <Route path="/welcome" element={<WelcomePage />} />
                        </Routes>
                    </Suspense>

                    <Suspense fallback={<CircularProgress />}>
                        <Routes>
                            <Route path="/cover" element={<MainPageCover />} />

                            <Route path="/skrach/view/:skrachId" element={<ViewSkrach />} />
                            <Route path="/skrach/view/:skrachId/:title" element={<ViewSkrach />} />
                            <Route path="/quiz/:category/:skrachId/:title" element={<ViewSkrach />} />
                            <Route path="/skrach/private/:skrachId/:pubLink" element={<ViewSkrach />} />
                            <Route path="/play" element={<GameField />} />
                            <Route path="/play/mobile/" element={<GameField />} />
                            <Route path="/skrach/score/:scoreId" element={<ViewScoreboard />} />
                            <Route path="/wizard" element={<SkrachWizard />} />

                            <Route path="/quiz/:categorySlug" element={<SearchCategory />} />
                            <Route path="/streamers" element={<StreamerPage />} />
                            <Route path="/fresh" element={<FreshSkraches />} />
                            <Route path="/trending" element={<TrendingSkraches />} />
                            <Route path="/top" element={<TopRatedSkraches />} />
                            <Route path="/browse/*" element={<BrowsePage />} />
                            <Route path="/tags/*" element={<TagsPage />} />
                            <Route path="/aboutus" element={<AboutUs />} />

                            <Route path="/live" element={<LiveStreamPage />} />

                            <Route path="/vote/:roomId" element={<ViewerVotePanel />} />
                            <Route path="/link/room/:currentId" element={<CreateRoomPanel />} />
                            <Route path="/login" element={<SignInPage />} />
                            <Route path="/skraches/user/:userId" element={<UserSkraches />} />

                            <Route path="/memory/:skrachId" element={<MemoryGame />} />

                            <Route element={<RequireAuth />}>
                                <Route path="/user/skraches" element={<UserSkraches />} />
                                <Route path="/skrach/edit/:skrachId" element={<EditSkrach />} />
                                <Route path="/skrach/questions/:skrachId" element={<CreateQuestion />} />
                                <Route path="/create/room" element={<CreateRoomPanel />} />
                                <Route path="/createNickname" element={<CreateNickname />} />
                                <Route path="/feedback" element={<FeedbackPage />} />
                            </Route>

                            <Route element={<RequireAdmin />}>
                                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                                <Route path="/admin/category/create" element={<CreateCategory />} />
                                <Route path="/admin/category/edit/:categoryId" element={<CreateCategory />} />
                                <Route path="/admin/skrach/list" element={<ListAllSkrach />} />
                                <Route path="/admin/waiting/list" element={<ListWaitingForApproval />} />
                                <Route path="/admin/gameMode/create" element={<CreateGameMode />} />
                                <Route path="/admin/gameMode/edit/:gameModeId" element={<CreateGameMode />} />
                                <Route path="/admin/feedback" element={<ListFeedback />} />
                                <Route path="/admin/feedback/:id" element={<ViewFeedback />} />
                                <Route path="/admin/scores" element={<ListScores />} />

                                <Route path="/admin/carousels" element={<AddCarousel/>} />
                                <Route path="/admin/carousels/:id" element={<AddCarousel/>} />

                                <Route path="/autoplay" element={<AutoplayMain />} />
                                <Route path="/autoplay/game" element={<GameFieldAutoplay />} />
                                <Route path="/autoplay/manual" element={<GameFieldAutoplayManual />} />

                                <Route path="/compact" element={<GameFieldShorts />} />
                                <Route path="/compact/autoplay" element={<GameFieldShorts autoPlay={true} />} />
                            </Route>
                        </Routes>
                    </Suspense>
                    <ShowSnack
                        snackOpen={snackOpen}
                        setSnackOpen={setSnackOpen}
                        message={'Install this app to your phone: <h3>Tap: <b>Share</b> → <b>Add to Home Screen</b></h3>'}
                        severity={'info'}
                    />
                </Grid>
            </Grid>

            {!isOnPath('/play') && !isPathStartsWith('/autoplay') && <Footer />}
        </div>
    );
}

export default App;
